import React from "react";
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    MuiIcons
} from "@leap4/frontend";
import { formatDate, parseUserAgent } from "../../../utils/format.utils";

const { LockOpen: LockOpenIcon, Lock: LockIcon } = MuiIcons;

export interface ActivityLogEntry {
    action: 'LOGIN' | 'LOGOUT';
    timestamp: string;
    ipAddress?: string;
    userAgent?: string;
    serviceName?: string;
}

export interface LastLoginInfo {
    date: string | Date;
    serviceName?: string;
}

interface ActivityLogsTabProps {
    lastLoginAt?: string | Date | LastLoginInfo;
    activityLog: ActivityLogEntry[];
}

const ActivityLogsTab = ({ lastLoginAt, activityLog }: ActivityLogsTabProps): JSX.Element => {
    const hasActivity = activityLog && activityLog.length > 0;

    // Handle both old format (Date/string) and new format ({ date, serviceName })
    const lastLoginDate: string | Date | undefined = lastLoginAt && typeof lastLoginAt === 'object' && 'date' in lastLoginAt
        ? lastLoginAt.date
        : (lastLoginAt as string | Date | undefined);
    const lastLoginService = lastLoginAt && typeof lastLoginAt === 'object' && 'serviceName' in lastLoginAt
        ? lastLoginAt.serviceName
        : undefined;

    return (
        <Box sx={{ p: 3 }}>
            {/* Last Login Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Last Login
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {formatDate(lastLoginDate)}
                    {lastLoginService && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({lastLoginService})
                        </Typography>
                    )}
                </Typography>
            </Box>

            {/* Activity Log Table */}
            <Typography variant="h6" gutterBottom>
                Activity History
            </Typography>

            {!hasActivity ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    No activity recorded yet.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Service</TableCell>
                                    <TableCell>IP Address</TableCell>
                                    <TableCell>Device</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activityLog.map((entry, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Chip
                                                icon={entry.action === 'LOGIN' ? <LockOpenIcon /> : <LockIcon />}
                                                label={entry.action}
                                                size="small"
                                                color={entry.action === 'LOGIN' ? 'success' : 'default'}
                                                sx={{ minWidth: 90 }}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(entry.timestamp)}</TableCell>
                                        <TableCell>{entry.serviceName || '-'}</TableCell>
                                        <TableCell>{entry.ipAddress || '-'}</TableCell>
                                        <TableCell>{parseUserAgent(entry.userAgent)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Showing {activityLog.length} most recent activities (max 100)
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default ActivityLogsTab;
